"use client";

import React, { type JSX, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetWaveData } from "@/contexts/TitleContext";
import { useContentTab } from "../ContentTabContext";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import MyStreamWaveChat from "./MyStreamWaveChat";
import MyStreamWaveCurationContent from "./curations/MyStreamWaveCurationContent";
import { useWaveData } from "@/hooks/useWaveData";
import MyStreamWaveLeaderboard from "./MyStreamWaveLeaderboard";
import MyStreamWaveSubmissions from "./MyStreamWaveSubmissions";
import MyStreamWaveOutcome from "./MyStreamWaveOutcome";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { WaveWinners } from "@/components/waves/winners/WaveWinners";
import { MyStreamWaveTab } from "@/types/waves.types";
import { MyStreamWaveTabs } from "./tabs/MyStreamWaveTabs";
import MyStreamWaveMyVotes from "./votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "./MyStreamWaveFAQ";
import MyStreamWaveSales from "./MyStreamWaveSales";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { createBreakpoint } from "react-use";
import { getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveViewMode } from "@/hooks/useWaveViewMode";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getDropQueryKey } from "@/services/api/drop-api";

export interface MyStreamWaveProps {
  readonly waveId: string;
}

const getContentTabPanelId = (tab: MyStreamWaveTab): string =>
  `my-stream-wave-tabpanel-${tab.toLowerCase()}`;

const useBreakpoint = createBreakpoint({ LG: 1024, S: 0 });

const MyStreamWaveContent: React.FC<MyStreamWaveProps> = ({ waveId }) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const { isApp } = useDeviceInfo();
  const queryClient = useQueryClient();
  const { waves, directMessages, registerWave } = useMyStream();
  const { data: wave } = useWaveData({
    waveId,
    onWaveNotFound: () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp,
      });
      const newUrl = params.toString()
        ? `${basePath}?${params.toString()}`
        : basePath || getHomeRoute();
      router.push(newUrl, { scroll: false });
    },
  });

  useEffect(() => {
    registerWave(waveId, true);
  }, [registerWave, waveId]);

  // Get enhanced data from the waves list (has correct WS-updated values)
  const enhancedData = useMemo(() => {
    const waveFromList =
      waves.list.find((w) => w.id === waveId) ??
      directMessages.list.find((w) => w.id === waveId);
    return {
      newDropsCount: waveFromList?.newDropsCount.count ?? 0,
      firstUnreadSerialNo: waveFromList?.firstUnreadDropSerialNo ?? null,
    };
  }, [waves.list, directMessages.list, waveId]);

  const newDropsCount = enhancedData.newDropsCount;

  // Update wave data in title context
  useSetWaveData(
    wave ? { name: wave.name, newItemsCount: newDropsCount } : null
  );

  // Create a stable key for proper remounting
  const stableWaveKey = `wave-${waveId}`;

  // Get the active tab and utilities from global context
  const { activeContentTab } = useContentTab();
  const activeCurationId = searchParams.get("curation");

  // View mode for chat/gallery toggle
  const { viewMode, setViewMode, toggleViewMode } = useWaveViewMode(waveId);

  // Get wave type info to determine if gallery toggle should be shown
  // Show for CHAT type waves (normal waves), hide for RANK, MEMES, and DMs
  const { isRankWave, isMemesWave, isDm } = useWave(wave);
  const showGalleryToggle = !isRankWave && !isMemesWave && !isDm;
  const hasSerialTarget = searchParams.get("serialNo") !== null;

  useEffect(() => {
    if (
      !wave ||
      !hasSerialTarget ||
      !showGalleryToggle ||
      viewMode !== "gallery"
    ) {
      return;
    }

    setViewMode("chat");
  }, [hasSerialTarget, setViewMode, showGalleryToggle, viewMode, wave]);

  useBreakpoint();

  // For handling clicks on drops
  const onDropClick = (drop: ExtendedDrop) => {
    queryClient.setQueryData<ApiDrop>(
      getDropQueryKey(drop.id),
      drop as ApiDrop
    );
    const params = new URLSearchParams(searchParams.toString() || "");
    params.set("drop", drop.id);
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };

  const onSelectCuration = (curationId: string | null) => {
    const params = new URLSearchParams(searchParams.toString() || "");

    if (curationId) {
      params.set("curation", curationId);
    } else {
      params.delete("curation");
    }

    const nextQuery = params.toString();
    const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
    const currentQuery = searchParams.toString();
    const currentUrl = currentQuery ? `${pathname}?${currentQuery}` : pathname;
    if (nextUrl === currentUrl) {
      return;
    }

    router.replace(nextUrl, { scroll: false });
  };

  // Early return if no wave data - all hooks must be called before this
  if (!wave) {
    return null;
  }

  // Create component instances with wave-specific props and stable measurements
  const components: Record<MyStreamWaveTab, JSX.Element> = {
    [MyStreamWaveTab.CHAT]: (
      <MyStreamWaveChat
        wave={wave}
        firstUnreadSerialNo={enhancedData.firstUnreadSerialNo}
        viewMode={showGalleryToggle ? viewMode : "chat"}
        onDropClick={onDropClick}
      />
    ),
    [MyStreamWaveTab.LEADERBOARD]: (
      <MyStreamWaveLeaderboard
        key={wave.id}
        wave={wave}
        onDropClick={onDropClick}
      />
    ),
    [MyStreamWaveTab.SUBMISSIONS]: (
      <MyStreamWaveSubmissions wave={wave} onDropClick={onDropClick} />
    ),
    [MyStreamWaveTab.SALES]: <MyStreamWaveSales waveId={wave.id} />,
    [MyStreamWaveTab.WINNERS]: (
      <WaveWinners wave={wave} onDropClick={onDropClick} />
    ),
    [MyStreamWaveTab.OUTCOME]: <MyStreamWaveOutcome wave={wave} />,
    [MyStreamWaveTab.MY_VOTES]: (
      <MyStreamWaveMyVotes wave={wave} onDropClick={onDropClick} />
    ),
    [MyStreamWaveTab.FAQ]: <MyStreamWaveFAQ wave={wave} />,
  };

  return (
    <div
      className="tailwind-scope tw-relative tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col"
      key={stableWaveKey}
    >
      {/* Always render tab container (hidden on app inside MyStreamWaveTabs) */}
      <MyStreamWaveTabs
        wave={wave}
        viewMode={viewMode}
        onToggleViewMode={toggleViewMode}
        showGalleryToggle={showGalleryToggle}
        activeCurationId={activeCurationId}
        onSelectCuration={onSelectCuration}
      />

      <div
        className="tw-relative tw-min-h-0 tw-min-w-0 tw-flex-grow tw-overflow-hidden"
        role="tabpanel"
        id={
          activeCurationId
            ? `my-stream-wave-tabpanel-curation-${activeCurationId}`
            : getContentTabPanelId(activeContentTab)
        }
      >
        {activeCurationId ? (
          <MyStreamWaveCurationContent
            key={activeCurationId}
            wave={wave}
            curationId={activeCurationId}
            onDropClick={onDropClick}
          />
        ) : (
          components[activeContentTab]
        )}
      </div>
    </div>
  );
};

export default MyStreamWaveContent;
